'use no memo';

import type { LocationPublic } from '@repo/types/locations';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetLocations } from '@repo/api/location/useGetLocations';
import { EditIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';

import CreateLocation from '@/components/features/manage/manage-locations/CreateLocation';
import DeleteLocation from '@/components/features/manage/manage-locations/DeleteLocation';
import EditLocation from '@/components/features/manage/manage-locations/EditLocation';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { useTableRowSelection } from '@/hooks/table';

export default function ManageLocationsPage() {
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openLocationAdd, setOpenLocationAdd] = useState<boolean>(false);

  const [activeLocation, setActiveLocation] = useState<LocationPublic | undefined>(undefined);

  const [rowSelection, setRowsSelection] = useState<RowSelectionState>({});

  const { data = [], isPending } = useGetLocations();

  const columns = useMemo<ColumnDef<LocationPublic>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Miejsce',
        id: 'name',
        accessorKey: 'name',
        header: 'Miejsce',
      },
      {
        meta: 'Opis',
        id: 'desc',
        accessorKey: 'desc',
        header: () => {
          return (
            <Button size={'sm'} variant={'ghost'} className={'w-fit'}>
              Opis
            </Button>
          );
        },
        cell: ({ getValue }) => {
          const desc = getValue();
          if (!desc) {
            return <p>-</p>;
          }
          return desc;
        },
      },
      {
        meta: 'Współrzędne',
        id: 'coordinates',
        accessorKey: 'coordinates',
        header: 'Współrzędne',
      },
      {
        meta: 'Adres',
        id: 'address',
        accessorKey: 'address',
        header: 'Adres',
        cell: ({ getValue }) => {
          const address = getValue();
          if (!address) {
            return <p>-</p>;
          }
          return address;
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;
          return (
            <div className={'flex w-full justify-end'}>
              <Button
                size={'sm'}
                variant={'ghost'}
                variantColor={'destructive'}
                onClick={() => {
                  setActiveLocation(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <Button
                size={'sm'}
                variant={'ghost'}
                onClick={() => {
                  setActiveLocation(rowOrigin);
                  setOpenEdit(true);
                }}
              >
                <EditIcon />
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'locations',
    columns,
    data,
    getRowId: (row) => String(row.id),
    rowSelection: rowSelection,
    onRowSelectionChange: setRowsSelection,
    defaultColumnVisibility: {
      desc: false,
    },
  });

  const selectedLocations = useMemo<LocationPublic[]>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds.map(Number));
    return data.filter((group) => idSet.has(group.id));
  }, [rowSelection, data]);

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection: setRowsSelection,
  });

  const addGroup = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Dodaj Lokację',
        icon: <PlusIcon className="size-4" />,
        func: () => setOpenLocationAdd(true),
      },
    ];
  }, []);

  return (
    <>
      <CreateLocation open={openLocationAdd} onOpenChange={setOpenLocationAdd} />
      <Dialog
        open={openEdit}
        onOpenChange={(open) => {
          setOpenEdit(open);
          if (!open) setActiveLocation(undefined);
        }}
      >
        <EditLocation
          open={openEdit}
          onOpenChange={(open) => {
            setOpenEdit(open);
            if (!open) setActiveLocation(undefined);
          }}
          location={activeLocation || selectedLocations[0]}
        />
      </Dialog>
      <DeleteLocation
        locations={activeLocation ? [activeLocation] : selectedLocations}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActiveLocation(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Lokacje'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            filterBy={[]}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            addGroup={addGroup}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
    </>
  );
}
